/**
 * Roadmap (tracks) service.
 * Loads tracks from content_catalog (contentType === 'track') or from tracks collection.
 * Loads nodes for a track from tracks/{trackId}/nodes.
 *
 * Client-side subscription: full catalog is visible; only Programming Fundamentals (per-language slugs/titles — see
 * `utils/roadmapSubscription.js`) is usable without premium (RoadmapView, Home SRS).
 */

import { db } from '../firebase/firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  arrayUnion,
  onSnapshot
} from 'firebase/firestore';

/** In-session dedupe: roadmap menu + graph hammer the same tracks/paths in parallel. */
const trackNodesPromiseCache = new Map();
const learningPathEntriesPromiseCache = new Map();
let publishedTracksPromise = null;
let publishedLearningPathsPromise = null;

/** Parallel getDoc batch size for snippet loading (this Firebase SDK has no getAll export). */
const SNIPPET_FETCH_CONCURRENCY = 10;

/** In-session cache for shared snippet documents (content rarely changes). */
const snippetDocCache = new Map();

/**
 * In-session cache for verified roadmap cookbooks keyed by userId:trackId:nodeId.
 * Stores cards only after membership sync + metadata are confirmed.
 * @type {Map<string, { cookbookId: string, snippetIdsKey: string, cards: object[] }>}
 */
const roadmapCookbookSessionCache = new Map();

function roadmapCookbookCacheKey(userId, trackId, nodeId) {
  return `${userId}:${trackId}:${nodeId}`;
}

function snippetIdsCacheKey(snippetIds) {
  return uniqueSnippetIdsInOrder(snippetIds).join(',');
}

function parseSnippetDoc(snap) {
  if (!snap.exists()) return null;
  const d = snap.data();
  const code = d.codeSnippet || d.code || d.solution || '';
  return {
    id: snap.id,
    title: d.snippetName || d.title || 'Snippet',
    code: code.trim()
  };
}

function deckHasRoadmapMetadata(deck, trackId, nodeId) {
  if (!deck || typeof deck !== 'object') return false;
  const tid = String(trackId || '').trim();
  const nid = String(nodeId || '').trim();
  if (!deck.trackId || !deck.roadmapTrackId) return false;
  if (!nid) return true;
  return Boolean(deck.nodeId && deck.roadmapNodeId);
}

function cardsHaveRoadmapMetadata(cards, trackId, nodeId) {
  if (!Array.isArray(cards) || cards.length === 0) return false;
  const nid = String(nodeId || '').trim();
  return cards.every((card) => {
    if (!card?.trackId || !card?.roadmapTrackId) return false;
    if (!nid) return true;
    return Boolean(card.nodeId && card.roadmapNodeId);
  });
}

async function loadCookbookCards(userId, cookbookId) {
  const cardsRef = collection(db, `users/${userId}/decks/${cookbookId}/cards`);
  const cardsSnap = await getDocs(cardsRef);
  return cardsSnap.docs.map((cardDoc) => ({ id: cardDoc.id, ...cardDoc.data() }));
}

function cacheRoadmapCookbook(userId, trackId, nodeId, snippetIds, cookbookId, cards) {
  const snippetIdsKey = snippetIdsCacheKey(snippetIds);
  roadmapCookbookSessionCache.set(roadmapCookbookCacheKey(userId, trackId, nodeId), {
    cookbookId,
    snippetIdsKey,
    cards
  });
}

function getCachedRoadmapCookbook(userId, trackId, nodeId, snippetIds) {
  const cached = roadmapCookbookSessionCache.get(roadmapCookbookCacheKey(userId, trackId, nodeId));
  if (!cached) return null;
  if (cached.snippetIdsKey !== snippetIdsCacheKey(snippetIds)) return null;
  return cached;
}

/**
 * Warn when multiple roadmap listings reference the same physical track (`trackId` / `contentId`)
 * but different `language` values — nodes and `track_progress` are keyed by `trackId`, so progress
 * cannot diverge per language until each language has its own `tracks/{trackId}` document.
 */
/**
 * Firestore ordering fields should be ints but may arrive as doubles or legacy strings after Excel sync.
 * @param {unknown} raw
 * @returns {number}
 */
function normalizeFirestoreOrderingNumber(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.trunc(raw);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }
  return 0;
}

function warnIfSharedTrackListingsConflict(tracks) {
  if (!tracks?.length) return;
  const byTrackId = new Map();
  for (const t of tracks) {
    const tid = t.trackId;
    if (!tid) continue;
    if (!byTrackId.has(tid)) byTrackId.set(tid, []);
    byTrackId.get(tid).push(t);
  }
  for (const [, group] of byTrackId) {
    if (group.length < 2) continue;
    const langKeys = new Set(
      group.map((g) => (g.language != null ? String(g.language).trim().toLowerCase() : '')).filter(Boolean)
    );
    if (langKeys.size > 1) {
      console.warn(
        '[Roadmap] Multiple listings share the same trackId with different languages. ' +
          'Progress is stored per trackId, so language rows will show the same position. ' +
          'Publish a distinct Firestore track (unique slug) for each language.',
        group.map((g) => ({ id: g.id, trackId: g.trackId, language: g.language, title: g.title }))
      );
    }
  }
}

/** EXP to level formula (matches LearnMode / LevelingSystem). */
function calculateExpToLevel(level) {
  return Math.round(100 * Math.pow(10, (level - 1) / (99 / Math.log(10))));
}

/**
 * Read programming language from a track or catalog doc (field names vary across content).
 * @param {Record<string, unknown>} d
 * @returns {string|null}
 */
function resolveTrackLanguageFromDoc(d) {
  if (!d || typeof d !== 'object') return null;
  const raw =
    d.language ??
    d.lang ??
    d.programmingLanguage ??
    d.programming_language;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return String(raw);
  if (typeof raw === 'object') {
    if (typeof raw.language === 'string') return raw.language;
    if (typeof raw.id === 'string') return raw.id;
  }
  return String(raw);
}

/**
 * Load published tracks. Tries the `tracks` collection first (single-field query, no composite index).
 * Then tries content_catalog (contentType === 'track') for catalog-backed discovery.
 * @returns {Promise<Array<{ id: string, contentId: string, trackId: string, title: string, description?: string, language?: string }>>}
 */
export async function loadPublishedTracks() {
  if (publishedTracksPromise) {
    return publishedTracksPromise;
  }

  publishedTracksPromise = (async () => {
  // Try tracks collection first – simpler query, only needs firestore rules for tracks
  try {
    const tracksRef = collection(db, 'tracks');
    const tracksQuery = query(
      tracksRef,
      where('status', '==', 'published'),
      limit(500)
    );
    const trackSnapshot = await getDocs(tracksQuery);
    const fromTracks = trackSnapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        contentId: doc.id,
        trackId: doc.id,
        title: d.title || 'Untitled Track',
        description: d.description || '',
        language: resolveTrackLanguageFromDoc(d) || null
      };
    });
    if (fromTracks.length > 0) {
      warnIfSharedTrackListingsConflict(fromTracks);
      return fromTracks;
    }
  } catch (tracksErr) {
    console.warn('Roadmap: loading from tracks collection failed', tracksErr);
    // Fall through to try catalog
  }

  // Fallback: content_catalog (requires composite index on status + contentType if many docs)
  try {
    const catalogRef = collection(db, 'content_catalog');
    const catalogQuery = query(
      catalogRef,
      where('status', '==', 'published'),
      where('contentType', '==', 'track'),
      limit(500)
    );
    const snapshot = await getDocs(catalogQuery);
    const fromCatalog = snapshot.docs.map(doc => {
      const d = doc.data();
      return {
        id: doc.id,
        contentId: d.contentId,
        trackId: d.contentId,
        title: d.title || 'Untitled Track',
        description: d.description || '',
        language: resolveTrackLanguageFromDoc(d) || null
      };
    });
    warnIfSharedTrackListingsConflict(fromCatalog);
    return fromCatalog;
  } catch (catalogErr) {
    console.error('Roadmap: loading from content_catalog failed', catalogErr);
    throw catalogErr;
  }
  })().catch((err) => {
    publishedTracksPromise = null;
    throw err;
  });

  return publishedTracksPromise;
}

/**
 * Whether this track can appear on Learn roadmaps: published in `tracks`, or (if no root doc)
 * listed as a published track in `content_catalog`. Draft, deleted, or test-only tracks → false.
 * @param {string} trackId
 * @returns {Promise<boolean>}
 */
export async function isTrackPublishedForLearn(trackId) {
  if (!trackId) return false;
  try {
    const trackRef = doc(db, 'tracks', trackId);
    const snap = await getDoc(trackRef);
    if (snap.exists()) {
      return snap.data().status === 'published';
    }
  } catch (e) {
    console.warn('isTrackPublishedForLearn: tracks read failed', trackId, e);
  }
  try {
    const catalogRef = collection(db, 'content_catalog');
    const catalogQuery = query(
      catalogRef,
      where('status', '==', 'published'),
      where('contentType', '==', 'track'),
      where('contentId', '==', trackId),
      limit(1)
    );
    const snapshot = await getDocs(catalogQuery);
    return !snapshot.empty;
  } catch (e) {
    console.warn('isTrackPublishedForLearn: catalog query failed', trackId, e);
    return false;
  }
}

/**
 * Load all nodes for a track from tracks/{trackId}/nodes.
 * @param {string} trackId
 * @returns {Promise<Array<{ id: string, title: string, description?: string, parent: string|null, order: number, snippetIds: string[], trackId: string }>>}
 */
export async function loadTrackNodes(trackId) {
  if (!trackId) return [];
  let p = trackNodesPromiseCache.get(trackId);
  if (!p) {
    p = (async () => {
      try {
        const nodesRef = collection(db, 'tracks', trackId, 'nodes');
        const snapshot = await getDocs(nodesRef);
        return snapshot.docs.map((docSnap) => {
          const d = docSnap.data();
          return {
            id: d.id ?? docSnap.id,
            title: d.title || 'Untitled Node',
            description: d.description || '',
            parent: d.parent ?? null,
            order: normalizeFirestoreOrderingNumber(d.order),
            snippetIds: Array.isArray(d.snippetIds) ? d.snippetIds : [],
            trackId: d.trackId || trackId
          };
        });
      } catch (err) {
        console.warn('Roadmap: loadTrackNodes failed', trackId, err);
        return [];
      }
    })();
    trackNodesPromiseCache.set(trackId, p);
  }
  return p;
}

/**
 * Normalize the `nodeLastAttempts` field read from Firestore.
 * Each entry: { percentage: number (0-100), passed: boolean, at?: Date|Timestamp }.
 * @param {*} raw
 * @returns {Record<string, { percentage: number, passed: boolean, at: Date | null }>}
 */
function normalizeNodeLastAttempts(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [nodeId, entry] of Object.entries(raw)) {
    if (!entry || typeof entry !== 'object') continue;
    const pctRaw = typeof entry.percentage === 'number' ? entry.percentage : Number(entry.percentage);
    if (!Number.isFinite(pctRaw)) continue;
    const percentage = Math.min(100, Math.max(0, Math.round(pctRaw)));
    const passed = Boolean(entry.passed);
    let at = null;
    if (entry.at) {
      if (typeof entry.at.toDate === 'function') at = entry.at.toDate();
      else if (entry.at instanceof Date) at = entry.at;
    }
    out[nodeId] = { percentage, passed, at };
  }
  return out;
}

/**
 * Load user progress for a track (completed nodes, pass percentages, last test attempts).
 * Path: users/{userId}/track_progress/{trackId}. Returns empty completedNodeIds if doc missing.
 * @param {string} userId
 * @param {string} trackId
 * @returns {Promise<{ completedNodeIds: string[], nodeScores: Record<string, number>, nodeLastAttempts: Record<string, { percentage: number, passed: boolean, at: Date | null }> }>}
 */
export async function loadTrackProgress(userId, trackId) {
  const empty = { completedNodeIds: [], nodeScores: {}, nodeLastAttempts: {} };
  if (!userId || !trackId) return empty;
  const progressRef = doc(db, 'users', userId, 'track_progress', trackId);
  const readFromSnap = (snap) => {
    if (!snap.exists()) return empty;
    const d = snap.data();
    return {
      completedNodeIds: Array.isArray(d.completedNodeIds) ? d.completedNodeIds : [],
      nodeScores: d.nodeScores && typeof d.nodeScores === 'object' ? d.nodeScores : {},
      nodeLastAttempts: normalizeNodeLastAttempts(d.nodeLastAttempts)
    };
  };
  try {
    return readFromSnap(await getDoc(progressRef, { source: 'server' }));
  } catch (serverErr) {
    try {
      return readFromSnap(await getDoc(progressRef));
    } catch (err) {
      console.warn('Roadmap: loadTrackProgress failed', err);
      return empty;
    }
  }
}

/**
 * Subscribe to user's track progress (real-time). Updates when markNodeCompleted or
 * recordNodeTestAttempt runs.
 * Only applies updates when snapshot is from the server (not cache) so returning to the roadmap
 * doesn't briefly show stale cached data that resets completed nodes.
 * @param {string} userId
 * @param {string} trackId
 * @param {(data: { completedNodeIds: string[], nodeScores: Record<string, number>, nodeLastAttempts: Record<string, { percentage: number, passed: boolean, at: Date | null }> }) => void} onUpdate
 * @returns {() => void} Unsubscribe function
 */
export function subscribeToTrackProgress(userId, trackId, onUpdate) {
  const empty = { completedNodeIds: [], nodeScores: {}, nodeLastAttempts: {} };
  if (!userId || !trackId) {
    onUpdate(empty);
    return () => { };
  }
  const progressRef = doc(db, 'users', userId, 'track_progress', trackId);
  const unsubscribe = onSnapshot(progressRef, (snap) => {
    if (!snap.exists()) {
      onUpdate(empty);
      return;
    }
    const d = snap.data();
    onUpdate({
      completedNodeIds: Array.isArray(d.completedNodeIds) ? d.completedNodeIds : [],
      nodeScores: d.nodeScores && typeof d.nodeScores === 'object' ? d.nodeScores : {},
      nodeLastAttempts: normalizeNodeLastAttempts(d.nodeLastAttempts)
    });
  }, (err) => {
    console.warn('Roadmap: subscribeToTrackProgress error', err);
    onUpdate(empty);
  });
  return unsubscribe;
}

/**
 * Mark a node as completed for a track (test mode). Appends nodeId to completedNodeIds and stores pass percentage.
 * Creates the progress doc if it does not exist.
 * @param {string} userId
 * @param {string} trackId
 * @param {string} nodeId
 * @param {number} [percentage] - Pass percentage (0–100) when test was passed; stored for display in roadmap.
 * @returns {Promise<void>}
 */
export async function markNodeCompleted(userId, trackId, nodeId, percentage = 100) {
  if (!userId || !trackId || !nodeId) {
    console.warn('Roadmap: markNodeCompleted skipped (missing id)', { userId: !!userId, trackId, nodeId });
    return;
  }
  const progressRef = doc(db, 'users', userId, 'track_progress', trackId);
  const score = Math.min(100, Math.max(0, Math.round(percentage)));
  try {
    const snap = await getDoc(progressRef);
    if (snap.exists()) {
      await updateDoc(progressRef, {
        completedNodeIds: arrayUnion(nodeId),
        [`nodeScores.${nodeId}`]: score,
        updatedAt: new Date()
      });
      console.log('Roadmap: markNodeCompleted updated doc', { trackId, nodeId, score });
    } else {
      await setDoc(progressRef, {
        completedNodeIds: [nodeId],
        nodeScores: { [nodeId]: score },
        updatedAt: new Date()
      }, { merge: true });
      console.log('Roadmap: markNodeCompleted created doc', { trackId, nodeId, score });
    }
  } catch (err) {
    console.error('Roadmap: markNodeCompleted failed', err);
    throw err;
  }

  try {
    const { maybeUnlockTrackAchievementAfterProgress } = await import('./achievementsService');
    await maybeUnlockTrackAchievementAfterProgress(userId, trackId);
  } catch (achErr) {
    console.warn('Roadmap: achievements unlock after node completion failed', achErr);
  }
}

/**
 * Record a node's most recent test attempt (pass or fail) for display in the roadmap.
 * Writes to `nodeLastAttempts.{nodeId} = { percentage, passed, at }` in the track_progress doc.
 * Independent of `markNodeCompleted` (which still runs only on a passing attempt to record
 * the trophy/completion). Failures call this without calling markNodeCompleted.
 * @param {string} userId
 * @param {string} trackId
 * @param {string} nodeId
 * @param {{ percentage: number, passed: boolean }} attempt
 * @returns {Promise<void>}
 */
export async function recordNodeTestAttempt(userId, trackId, nodeId, attempt) {
  if (!userId || !trackId || !nodeId || !attempt || typeof attempt.percentage !== 'number') {
    console.warn('Roadmap: recordNodeTestAttempt skipped (missing data)', {
      userId: !!userId, trackId, nodeId, attempt
    });
    return;
  }
  const progressRef = doc(db, 'users', userId, 'track_progress', trackId);
  const percentage = Math.min(100, Math.max(0, Math.round(attempt.percentage)));
  const passed = Boolean(attempt.passed);
  const entry = { percentage, passed, at: new Date() };
  try {
    const snap = await getDoc(progressRef);
    if (snap.exists()) {
      await updateDoc(progressRef, {
        [`nodeLastAttempts.${nodeId}`]: entry,
        updatedAt: new Date()
      });
    } else {
      await setDoc(progressRef, {
        nodeLastAttempts: { [nodeId]: entry },
        updatedAt: new Date()
      }, { merge: true });
    }
    console.log('Roadmap: recordNodeTestAttempt saved', { trackId, nodeId, percentage, passed });
  } catch (err) {
    console.error('Roadmap: recordNodeTestAttempt failed', err);
  }
}

/**
 * Preserve snippet order while dropping duplicate IDs (bad track data or legacy ID collisions).
 * @param {string[]} snippetIds
 * @returns {string[]}
 */
export function uniqueSnippetIdsInOrder(snippetIds) {
  if (!Array.isArray(snippetIds) || snippetIds.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const raw of snippetIds) {
    const id = String(raw || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Load snippet documents by ID from the shared snippets collection (roadmap snippets have nodeId/trackId).
 * Duplicate IDs in the input list are ignored after the first occurrence.
 * @param {string[]} snippetIds - Document IDs in the snippets collection
 * @returns {Promise<Array<{ id: string, title: string, code: string }>>}
 */
export async function loadSnippetsByIds(snippetIds) {
  const orderedUniqueIds = uniqueSnippetIdsInOrder(snippetIds);
  if (orderedUniqueIds.length === 0) return [];

  const missingIds = [];
  for (const id of orderedUniqueIds) {
    if (!snippetDocCache.has(id)) {
      missingIds.push(id);
    }
  }

  for (let i = 0; i < missingIds.length; i += SNIPPET_FETCH_CONCURRENCY) {
    const chunk = missingIds.slice(i, i + SNIPPET_FETCH_CONCURRENCY);
    const snaps = await Promise.all(
      chunk.map((id) => getDoc(doc(db, 'snippets', id)))
    );
    for (const snap of snaps) {
      const snippet = parseSnippetDoc(snap);
      if (snippet) {
        snippetDocCache.set(snippet.id, snippet);
      }
    }
  }

  return orderedUniqueIds
    .map((id) => snippetDocCache.get(id))
    .filter(Boolean);
}

/**
 * Whether a user deck document is the cookbook auto-created for a roadmap node (not a standalone library cookbook).
 * @param {{ roadmapNodeId?: string, roadmapTrackId?: string, tags?: unknown[] }} deck - Deck doc data (and optional id)
 * @returns {boolean}
 */
export function isRoadmapUserCookbook(deck) {
  if (!deck || typeof deck !== 'object') return false;
  if (deck.roadmapNodeId || deck.roadmapTrackId) return true;
  const tags = deck.tags;
  if (!Array.isArray(tags)) return false;
  return tags.some((t) => typeof t === 'string' && t.toLowerCase() === 'roadmap');
}

/**
 * Get the user's cookbook (deck) for a roadmap node if it exists.
 * Cookbooks created from roadmap nodes are stored with type 'cookbook', roadmapNodeId, and roadmapTrackId.
 * @param {string} userId
 * @param {string} trackId
 * @param {string} nodeId
 * @returns {Promise<{ id: string } | null>} Cookbook doc id and data, or null
 */
export async function getCookbookForNode(userId, trackId, nodeId) {
  if (!userId || !trackId || !nodeId) return null;
  const decksRef = collection(db, `users/${userId}/decks`);
  const q = query(
    decksRef,
    where('type', '==', 'cookbook'),
    where('roadmapNodeId', '==', nodeId),
    where('roadmapTrackId', '==', trackId),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  return { id: d.id, ...d.data() };
}

async function deleteCookbookCards(userId, cookbookId) {
  const cardsRef = collection(db, `users/${userId}/decks/${cookbookId}/cards`);
  const snapshot = await getDocs(cardsRef);
  await Promise.all(snapshot.docs.map((cardDoc) => deleteDoc(cardDoc.ref)));
}

function buildCookbookCardData(snippet, node, trackId, timestamp, language = '') {
  const cardLanguage = (snippet?.language || language || '').trim();
  return {
    title: snippet.title || 'Snippet',
    description: `From ${node.title}`,
    solution: snippet.code,
    ...(cardLanguage ? { language: cardLanguage.toLowerCase() } : {}),
    tags: ['roadmap', trackId, node.title.toLowerCase().replace(/\s+/g, '_')],
    trackId,
    nodeId: node.id,
    roadmapNodeId: node.id,
    roadmapTrackId: trackId,
    roadmapSnippetId: snippet.id,
    level: 1,
    current_exp: 0,
    exp_to_level: calculateExpToLevel(2),
    currentLineIndex: 0,
    createdAt: timestamp,
    updatedAt: timestamp,
    creationSource: 'roadmap'
  };
}

async function addCookbookCardsFromSnippets(userId, cookbookId, node, snippets, trackId, timestamp, language = '') {
  const cardsRef = collection(db, `users/${userId}/decks/${cookbookId}/cards`);
  const seenSnippetIds = new Set();
  /** @type {Array<{ id: string } & Record<string, unknown>>} */
  const createdCards = [];
  for (const snippet of snippets) {
    if (!snippet?.id || seenSnippetIds.has(snippet.id)) continue;
    if (!snippet.code || !snippet.code.trim()) continue;
    seenSnippetIds.add(snippet.id);
    const cardData = buildCookbookCardData(snippet, node, trackId, timestamp, language);
    const cardRef = await addDoc(cardsRef, cardData);
    createdCards.push({ id: cardRef.id, ...cardData });
  }
  return createdCards;
}

/**
 * True when cookbook cards do not match the node's unique snippet IDs.
 * Membership only — Firestore card order is not stable and must not force a rebuild.
 * @param {Array<{ roadmapSnippetId?: string }>} cards
 * @param {string[]} expectedSnippetIds
 * @returns {boolean}
 */
export function cookbookCardsNeedRebuild(cards, expectedSnippetIds) {
  if (!Array.isArray(expectedSnippetIds) || expectedSnippetIds.length === 0) return false;
  if (!Array.isArray(cards) || cards.length === 0) return true;
  const cardSnippetIds = cards
    .map((card) => String(card.roadmapSnippetId || '').trim())
    .filter(Boolean);
  if (cardSnippetIds.length !== expectedSnippetIds.length) return true;
  if (new Set(cardSnippetIds).size !== cardSnippetIds.length) return true;
  const expectedSet = new Set(expectedSnippetIds);
  if (expectedSet.size !== expectedSnippetIds.length) return true;
  return cardSnippetIds.some((id) => !expectedSet.has(id));
}

/**
 * Prefer the card with the most study activity when deduping colliding snippet IDs.
 * @param {{ level?: number, current_exp?: number, lastCompleted?: unknown }} card
 * @returns {number}
 */
function cookbookCardActivityScore(card) {
  const level = Number(card?.level);
  const exp = Number(card?.current_exp);
  const completed = card?.lastCompleted ? 1 : 0;
  return (
    (Number.isFinite(level) ? level : 1) * 1000 +
    (Number.isFinite(exp) ? exp : 0) +
    completed
  );
}

/**
 * Sync cookbook cards to the node's snippet list without wiping progress.
 * Adds missing snippets, removes obsolete/duplicate cards + their progress docs.
 * Existing matching cards (and users/{uid}/progress/{deckId}_{cardId}) are left intact.
 */
async function syncRoadmapCookbookCards(
  userId,
  cookbookId,
  node,
  snippets,
  trackId,
  expectedSnippetIds,
  timestamp,
  language = ''
) {
  const cardsRef = collection(db, `users/${userId}/decks/${cookbookId}/cards`);
  const cardsSnap = await getDocs(cardsRef);
  const cards = cardsSnap.docs.map((cardDoc) => ({ id: cardDoc.id, ...cardDoc.data() }));

  const expectedSet = new Set(expectedSnippetIds);
  const snippetById = new Map();
  for (const snippet of snippets || []) {
    const id = String(snippet?.id || '').trim();
    if (!id || snippetById.has(id)) continue;
    if (!snippet?.code || !String(snippet.code).trim()) continue;
    snippetById.set(id, snippet);
  }

  /** @type {Map<string, Array<{ id: string } & Record<string, unknown>>>} */
  const cardsBySnippetId = new Map();
  const orphanCards = [];
  for (const card of cards) {
    const sid = String(card.roadmapSnippetId || '').trim();
    // Keep legacy cards missing roadmapSnippetId — deleting them would orphan SRS progress.
    if (!sid) continue;
    if (!expectedSet.has(sid)) {
      orphanCards.push(card);
      continue;
    }
    if (!cardsBySnippetId.has(sid)) cardsBySnippetId.set(sid, []);
    cardsBySnippetId.get(sid).push(card);
  }

  const toDelete = [...orphanCards];
  for (const [, group] of cardsBySnippetId) {
    if (group.length <= 1) continue;
    group.sort((a, b) => cookbookCardActivityScore(b) - cookbookCardActivityScore(a));
    toDelete.push(...group.slice(1));
  }

  await Promise.all(
    toDelete.map(async (card) => {
      await deleteDoc(doc(db, `users/${userId}/decks/${cookbookId}/cards`, card.id));
      try {
        await deleteDoc(doc(db, `users/${userId}/progress`, `${cookbookId}_${card.id}`));
      } catch (_) {
        /* progress doc may not exist */
      }
    })
  );

  const existingIds = new Set(
    [...cardsBySnippetId.entries()]
      .filter(([, group]) => group.length > 0)
      .map(([sid]) => sid)
  );
  for (const sid of expectedSnippetIds) {
    if (existingIds.has(sid)) continue;
    const snippet = snippetById.get(sid);
    if (!snippet) continue;
    await addDoc(cardsRef, buildCookbookCardData(snippet, node, trackId, timestamp, language));
  }
}

/**
 * Lazy backfill canonical `trackId` / `nodeId` on roadmap cookbook decks and cards.
 * @returns {Promise<{ cardsUpdated: number, deckUpdated: boolean }>}
 */
export async function backfillRoadmapCookbookTrackMetadata(userId, cookbookId, { trackId, nodeId }) {
  const tid = trackId != null ? String(trackId).trim() : '';
  const nid = nodeId != null ? String(nodeId).trim() : '';
  if (!userId || !cookbookId || !tid) {
    return { cardsUpdated: 0, deckUpdated: false };
  }

  let deckUpdated = false;
  const deckRef = doc(db, `users/${userId}/decks`, cookbookId);
  const deckSnap = await getDoc(deckRef);
  if (deckSnap.exists()) {
    const deckData = deckSnap.data();
    const deckPatch = {};
    if (!deckData.trackId) deckPatch.trackId = tid;
    if (!deckData.roadmapTrackId) deckPatch.roadmapTrackId = tid;
    if (nid && !deckData.nodeId) deckPatch.nodeId = nid;
    if (nid && !deckData.roadmapNodeId) deckPatch.roadmapNodeId = nid;
    if (Object.keys(deckPatch).length > 0) {
      await updateDoc(deckRef, deckPatch);
      deckUpdated = true;
    }
  }

  const cardsRef = collection(db, `users/${userId}/decks/${cookbookId}/cards`);
  const cardsSnap = await getDocs(cardsRef);
  let cardsUpdated = 0;
  await Promise.all(
    cardsSnap.docs.map(async (cardDoc) => {
      const cardData = cardDoc.data();
      const cardPatch = {};
      if (!cardData.trackId) cardPatch.trackId = tid;
      if (!cardData.roadmapTrackId) cardPatch.roadmapTrackId = tid;
      if (nid && !cardData.nodeId) cardPatch.nodeId = nid;
      if (nid && !cardData.roadmapNodeId) cardPatch.roadmapNodeId = nid;
      if (Object.keys(cardPatch).length === 0) return;
      await updateDoc(cardDoc.ref, cardPatch);
      cardsUpdated += 1;
    })
  );

  return { cardsUpdated, deckUpdated };
}

/**
 * Return the user's roadmap cookbook for a node, creating or syncing cards when needed.
 * Syncs when card snippet membership does not match the node's unique snippet list
 * (adds missing IDs, drops obsolete/duplicate IDs). Matching cards and their SRS progress
 * are preserved — never delete-all/recreate.
 *
 * @returns {Promise<{ cookbookId: string, cards: Array<{ id: string } & Record<string, unknown>> }>}
 */
export async function ensureRoadmapCookbookForNode(
  userId,
  trackId,
  node,
  snippets,
  language = 'javascript',
  trackTitle = ''
) {
  const expectedSnippetIds = (Array.isArray(snippets) ? snippets : [])
    .map((snippet) => String(snippet?.id || '').trim())
    .filter(Boolean);
  const uniqueExpectedIds = uniqueSnippetIdsInOrder(expectedSnippetIds);
  const uniqueSnippets = [];
  const seen = new Set();
  for (const snippet of snippets || []) {
    const id = String(snippet?.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    uniqueSnippets.push(snippet);
  }

  const cached = getCachedRoadmapCookbook(userId, trackId, node.id, uniqueExpectedIds);
  if (cached) {
    return { cookbookId: cached.cookbookId, cards: cached.cards };
  }

  const existing = await getCookbookForNode(userId, trackId, node.id);
  if (!existing?.id) {
    const cookbookId = await createCookbookFromNode(
      userId,
      trackId,
      node,
      uniqueSnippets,
      language,
      trackTitle
    );
    const cards = await loadCookbookCards(userId, cookbookId);
    cacheRoadmapCookbook(userId, trackId, node.id, uniqueExpectedIds, cookbookId, cards);
    return { cookbookId, cards };
  }

  const cards = await loadCookbookCards(userId, existing.id);

  if (!cookbookCardsNeedRebuild(cards, uniqueExpectedIds)) {
    const needsBackfill =
      !deckHasRoadmapMetadata(existing, trackId, node.id) ||
      !cardsHaveRoadmapMetadata(cards, trackId, node.id);
    if (needsBackfill) {
      await backfillRoadmapCookbookTrackMetadata(userId, existing.id, {
        trackId,
        nodeId: node.id
      });
      roadmapCookbookSessionCache.delete(roadmapCookbookCacheKey(userId, trackId, node.id));
      const refreshedCards = await loadCookbookCards(userId, existing.id);
      cacheRoadmapCookbook(userId, trackId, node.id, uniqueExpectedIds, existing.id, refreshedCards);
      return { cookbookId: existing.id, cards: refreshedCards };
    }

    cacheRoadmapCookbook(userId, trackId, node.id, uniqueExpectedIds, existing.id, cards);
    return { cookbookId: existing.id, cards };
  }

  roadmapCookbookSessionCache.delete(roadmapCookbookCacheKey(userId, trackId, node.id));

  const timestamp = new Date();
  await syncRoadmapCookbookCards(
    userId,
    existing.id,
    node,
    uniqueSnippets,
    trackId,
    uniqueExpectedIds,
    timestamp,
    language
  );
  await updateDoc(doc(db, `users/${userId}/decks`, existing.id), {
    updatedAt: timestamp,
    lastAccessed: timestamp
  });

  const syncedCards = await loadCookbookCards(userId, existing.id);
  cacheRoadmapCookbook(userId, trackId, node.id, uniqueExpectedIds, existing.id, syncedCards);
  return { cookbookId: existing.id, cards: syncedCards };
}

/**
 * Create a cookbook (deck) from a roadmap node's snippets and add cards to the user's library.
 * Mirrors LearnMode createCookbookFromCourse pattern.
 * @param {string} userId
 * @param {string} trackId
 * @param {{ id: string, title: string, description?: string }} node
 * @param {Array<{ id: string, title: string, code: string }>} snippets
 * @param {string} [language]
 * @param {string} [trackTitle] - Display name of the track (e.g. "Programming Roadmap")
 * @returns {Promise<string>} The new cookbook document id
 */
export async function createCookbookFromNode(userId, trackId, node, snippets, language = 'javascript', trackTitle = '') {
  if (!userId || !trackId || !node?.id) {
    throw new Error('userId, trackId, and node.id are required');
  }
  const timestamp = new Date();
  const deckLanguage = (language || 'javascript').toLowerCase();
  const languageLabel = deckLanguage.charAt(0).toUpperCase() + deckLanguage.slice(1);
  const trackName = (trackTitle || trackId).trim();
  const cookbookName = trackName ? `${node.title} - ${languageLabel} ${trackName}` : `${node.title} - ${languageLabel}`;
  const cookbookData = {
    name: cookbookName,
    description: node.description
      ? `Snippets from "${node.title}". ${node.description}`
      : `Practice code snippets from the "${node.title}" roadmap node.`,
    language: deckLanguage,
    type: 'cookbook',
    tags: ['roadmap', 'cookbook', trackId],
    trackId,
    nodeId: node.id,
    roadmapNodeId: node.id,
    roadmapTrackId: trackId,
    roadmapNodeTitle: node.title,
    level: 1,
    current_exp: 0,
    exp_to_level: calculateExpToLevel(2),
    createdAt: timestamp,
    updatedAt: timestamp,
    lastAccessed: timestamp,
    userId
  };

  const decksRef = collection(db, `users/${userId}/decks`);
  const cookbookRef = await addDoc(decksRef, cookbookData);
  const cookbookId = cookbookRef.id;

  await addCookbookCardsFromSnippets(userId, cookbookId, node, snippets, trackId, timestamp, deckLanguage);

  try {
    const { maybeUnlockTrackStarted } = await import('./achievementsService');
    await maybeUnlockTrackStarted(userId, trackId);
  } catch (achErr) {
    console.warn('Roadmap: track-started achievement failed', achErr);
  }

  return cookbookId;
}

/**
 * Load published learning paths (single-language curricula over tracks).
 * @returns {Promise<Array<{ pathId: string, title: string, description?: string, language: string, status: string }>>}
 */
export async function loadPublishedLearningPaths() {
  if (!publishedLearningPathsPromise) {
    publishedLearningPathsPromise = (async () => {
      try {
        const pathsRef = collection(db, 'learning_paths');
        const q = query(pathsRef, where('status', '==', 'published'), limit(200));
        const snapshot = await getDocs(q);
        return snapshot.docs.map((docSnap) => {
          const d = docSnap.data();
          return {
            pathId: docSnap.id,
            title: d.title || 'Learning path',
            description: d.description || '',
            language: (d.language || '').toLowerCase() || null,
            status: d.status || 'draft'
          };
        });
      } catch (err) {
        console.warn('Roadmap: loadPublishedLearningPaths failed', err);
        return [];
      }
    })();
  }
  return publishedLearningPathsPromise;
}

/**
 * Load entries for a learning path (tracks graph).
 * @param {string} pathId
 * @returns {Promise<Array<{ id: string, title: string, description?: string, parent: string|null, order: number, trackId: string, pathId: string }>>}
 */
export async function loadLearningPathEntries(pathId) {
  if (!pathId) return [];
  let p = learningPathEntriesPromiseCache.get(pathId);
  if (!p) {
    p = (async () => {
      try {
        const entriesRef = collection(db, 'learning_paths', pathId, 'entries');
        const snapshot = await getDocs(entriesRef);
        return snapshot.docs.map((docSnap) => {
          const d = docSnap.data();
          return {
            id: d.id ?? docSnap.id,
            title: (typeof d.title === 'string' ? d.title.trim() : '') || '',
            description: d.description || '',
            parent: d.parent ?? null,
            order: normalizeFirestoreOrderingNumber(d.order),
            trackId: d.trackId || '',
            pathId: d.pathId || pathId
          };
        });
      } catch (err) {
        console.warn('Roadmap: loadLearningPathEntries failed', pathId, err);
        return [];
      }
    })();
    learningPathEntriesPromiseCache.set(pathId, p);
  }
  return p;
}

/**
 * Load a single learning path document.
 * @param {string} pathId
 * @returns {Promise<{ pathId: string, title: string, description?: string, language: string, status: string } | null>}
 */
export async function loadLearningPath(pathId) {
  if (!pathId) return null;
  const ref = doc(db, 'learning_paths', pathId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    pathId: snap.id,
    title: d.title || 'Learning path',
    description: d.description || '',
    language: (d.language || '').toLowerCase() || '',
    status: d.status || 'draft'
  };
}
